import React, { useCallback, useState, useEffect } from 'react';
import { NextComponentType, NextPageContext } from 'next/types';
import { useRouter } from 'next/router';
import { Hydrate, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ShellContainer, ShellContainerConfig, ShellEvent } from '@appd/fso-ui-shell/container';
import { Uik } from '@appd/uik';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { getShellTopNavConfig } from '../../modules/fso-ui-shell/shellTopNavConfig';
import { config } from '../../appConfig';
import { getShellFeatureFlagsConfig } from '../../modules/feature-flags/shellFeatureFlagsConfig';

export interface AppPageProps {
  shellConfig: ShellContainerConfig;
  dehydratedState: unknown;
}

interface AppContentProps {
  Component: NextComponentType<NextPageContext, Record<string, unknown>, Record<string, unknown>>;
  pageProps: AppPageProps;
  basePath: string;
}

export const AppContent: React.FC<AppContentProps> = ({ Component, pageProps, basePath }) => {
  const [dashboardName, setDashboardName] = useState<string>('');
  const [hideTimeRangeSelector, setHideTimeRangeSelector] = useState<boolean>(false);

  const selectedTemplateChanged = useCallback((_dashboardTitle: string) => {
    setDashboardName(_dashboardTitle);
  }, []);

  const hideTimeRangeSelectorChanged = useCallback((_hideTimeRangeSelector: boolean) => {
    setHideTimeRangeSelector(_hideTimeRangeSelector ?? false);
  }, []);

  const router = useRouter();

  useEffect(() => {
    // Subscribe to the event when the component mounts
    ShellEvent.on('selectedTemplateName', selectedTemplateChanged);
    ShellEvent.on('hideTimeRangeSelector', hideTimeRangeSelectorChanged);

    // Unsubscribe from the event when the component unmounts
    return () => {
      ShellEvent.un('selectedTemplateName', selectedTemplateChanged);
      ShellEvent.un('hideTimeRangeSelector', hideTimeRangeSelectorChanged);
    };
  }, [selectedTemplateChanged, hideTimeRangeSelectorChanged]);

  const featureFlagsConfig = getShellFeatureFlagsConfig();

  const [queryClient] = useState(() => new QueryClient());

  const pagePropsShellConfig = pageProps.shellConfig ?? {
    devConfig: { tenantUrl: config.NEXT_PUBLIC_TENANT_URL },
    ...getShellTopNavConfig(dashboardName, hideTimeRangeSelector),
    ...featureFlagsConfig,
    router: router.push,
  };

  return (
    <Uik assetsPath={`${basePath}/uik`}>
      <ShellContainer config={pagePropsShellConfig}>
        <QueryClientProvider client={queryClient}>
          <Hydrate state={pageProps.dehydratedState}>
            <Component {...pageProps} />
          </Hydrate>
          <ReactQueryDevtools />
        </QueryClientProvider>
      </ShellContainer>
    </Uik>
  );
};
